import { ReadonlyVec2 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { Environment } from '~/engine/data/Environment';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { TextureView } from '../../../view/TextureView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { writeRefusal } from './editableView';
import { localization } from '~/i18n';
import { CanvasCircleCursor } from './CanvasCircleCursor';

/** Diameter range shared by every circle-cursor SDF stroke tool (mm). */
export const MIN_DIAMETER_MM = 2;
export const MAX_DIAMETER_MM = 50;

/**
 * The mutable state of one in-progress stroke, as this base class needs
 * to see it. The concrete state classes
 * ({@link ../../actions/contouring-actions/DeformAction.DeformActionState},
 * {@link ../../actions/contouring-actions/SmoothBrushAction.SmoothBrushActionState})
 * carry much more; only these two members are driven from here.
 */
export interface CircleCursorStrokeState {
    /** Advance the stroke to a new cursor position. */
    onMouseMove(coordinates: ReadonlyVec2): EventHandlerResult;
    /** Abandon the stroke, leaving the structure exactly as it was. */
    cancel(): void;
}

/** The per-stroke lifecycle object a circle-cursor tool creates. */
export interface CircleCursorStrokeAction<
    TState extends CircleCursorStrokeState,
> {
    startAction(view: PlaneView, coordinates: ReadonlyVec2): TState;
    /** Commit the stroke and report the resulting view update. */
    stopAction(state: TState): EventHandlerResult;
}

/**
 * Shared behaviour of the SDF stroke tools that paint through a circular
 * cursor — {@link DeformTool} and {@link SmoothBrushTool}.
 *
 * These tools differ only in what a stroke DOES: the interaction around
 * it is one contract, and a divergence in it would be a safety problem
 * rather than an inconsistency. Every element below is part of that
 * contract:
 *
 * - a stroke is refused on an approved structure, with the same
 *   explanation of how to proceed;
 * - a stroke starts only in a view of the armed Environment, and the
 *   hover cursor shows only there; a press or a held-button drag on a
 *   view of another Environment is refused with an explanation, and a
 *   tool armed with no Environment refuses every press (fail closed);
 * - a stroke starts only on the left button; a stroke confined to the shown
 *   plane additionally needs that plane to follow the image slices, since
 *   a plane cutting the grid obliquely has no slice of its own to confine
 *   to, and is refused with an explanation otherwise; the view is snapped
 *   to the image grid first;
 * - the cursor leaves a view the tool refuses ({@link refusalFor}), and a
 *   Shift+Wheel resize over such a view does not draw it there;
 * - a stroke takes moves from the view it started on only: a move over
 *   another view is ignored and the cursor leaves it, and the release
 *   commits the stroke wherever it happens;
 * - the stroke ends on mouse release AND on a move that arrives with no
 *   button held, so a release delivered outside the canvas cannot leave
 *   a stroke open and the structure locked;
 * - Escape abandons the stroke with no modification;
 * - the crosshair follows the shared contouring policy: reduced to its
 *   endpoint handles while the tool is active, blocked during a stroke,
 *   so a stroke started over a crosshair line cannot drag the line;
 * - deactivation commits an in-progress stroke rather than dropping it,
 *   and always clears the cursor overlay;
 * - Shift+Wheel resizes the brush on the same ladder, so the same
 *   gesture travels the same distance in either tool.
 *
 * A subclass supplies its own {@link toolType}, whatever extra settings
 * its stroke takes, and {@link createAction}.
 */
export abstract class CircleCursorStrokeTool<
    TState extends CircleCursorStrokeState,
    TAction extends CircleCursorStrokeAction<TState>,
> extends Tool {
    private static readonly SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    protected readonly structure: RegionOfInterest;
    /** The Environment whose views a stroke may start on. */
    private readonly environment: Environment | undefined;
    private _diameter: number;
    private readonly diameterChangedCallback: (diameter: number) => void;

    private _cursor?: CanvasCircleCursor;
    private _action?: TAction;
    private _state?: TState;
    private _activeView?: PlaneView;

    protected constructor(
        structure: RegionOfInterest,
        environment: Environment | undefined,
        diameter: number,
        diameterChangedCallback: (diameter: number) => void
    ) {
        super();
        this.structure = structure;
        this.environment = environment;
        this._diameter = clampDiameter(diameter);
        this.diameterChangedCallback = diameterChangedCallback;
    }

    /**
     * Create the action for one stroke, with the subclass's current
     * settings. Called on every press, so settings changed between
     * strokes take effect on the next one and never mid-stroke.
     */
    protected abstract createAction(): TAction;

    /**
     * Whether the next stroke is confined to the plane the view shows
     * (2D mode) rather than acting on a volume around the cursor.
     */
    protected abstract isStrokeConfinedToPlane(): boolean;

    public get diameter(): number {
        return this._diameter;
    }

    public set diameter(value: number) {
        const clamped = clampDiameter(value);
        if (clamped !== this._diameter) {
            this._diameter = clamped;
            this._cursor?.setDiameter(this._diameter);
        }
    }

    public override onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (!modifierKeys.equals(CircleCursorStrokeTool.SIZE_MODIFIER_KEYS)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this.diameter = this._diameter + delta * wheelStepMm(this._diameter);
        this._cursor?.setDiameter(this._diameter);
        if (this.showsCursorOn(view)) {
            this._cursor?.draw(view, coordinates);
        } else {
            this._cursor?.clear();
        }
        this.diameterChangedCallback(this._diameter);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public override onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModifyContours === false) {
            enqueueSnackbar(
                'Modifying approved structures is not allowed.' +
                    ' Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (button === MOUSE_BUTTON_LEFT) {
            const refusal = this.refusalFor(view);
            if (refusal !== undefined) {
                enqueueSnackbar(refusal, { variant: 'warning' });
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_NOT_HANDLED
                );
            }
            // A stroke whose release went unseen is ended first.
            this.finishStroke();
            view.snapToImagePixel();
            this._action = this.createAction();
            this._state = this._action.startAction(view, coordinates);
            this._activeView = view;
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this.isActionInProgress()) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: PlaneView
    ): EventHandlerResult {
        // A move with no button held means the release happened where we
        // could not see it (outside the canvas); end the stroke rather
        // than keep painting.
        if (buttons === MOUSE_BUTTONS_NONE && this._state) {
            return this.finishStroke();
        }

        if (this._state) {
            // The stroke writes through the plane it started on; a move over
            // another view is outside what that view shows.
            if (view !== this._activeView) {
                this._cursor?.clear();
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED
                );
            }
            this._state.onMouseMove(coordinates);
            this._cursor?.draw(view, coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        const refusal =
            view instanceof PlaneView && this.structure.canModifyContours
                ? this.refusalFor(view)
                : undefined;
        if (refusal !== undefined) {
            this._cursor?.clear();
            // A held-button drag across a refused view re-enters this branch
            // per move; the global snackbar preventDuplicate config collapses
            // the repeats. A plain hover stays silent.
            if (buttons !== MOUSE_BUTTONS_NONE) {
                enqueueSnackbar(refusal, { variant: 'warning' });
            }
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Cursor hover
        if (view instanceof PlaneView && this.showsCursorOn(view)) {
            if (!this._cursor) {
                this._cursor = new CanvasCircleCursor(
                    this._diameter,
                    this.structure.color
                );
            }
            this._cursor.draw(view, coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        if (this._state) {
            return this.finishStroke();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        if (key === 'Escape' && this._state) {
            try {
                this._state.cancel();
            } finally {
                this.clearStroke();
            }
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                {
                    layers: LayerBit.CONTOUR_LAYER_BIT,
                    volume: this.structure.boundingBox,
                }
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override isActionInProgress(): boolean {
        return this._state !== undefined;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._activeView : undefined;
    }

    /**
     * Cover the full image grid so a stroke near the current content
     * bounds has room to operate.
     */
    public override onActivate(): EventHandlerResult {
        const texture = this.structure.texture;
        if (texture && !texture.isEmpty) {
            texture.expandToImageGrid();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onDeactivate(): EventHandlerResult {
        try {
            return this._state && this._action
                ? this.finishStroke()
                : new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        } finally {
            this._cursor?.clear();
            this._cursor = undefined;
        }
    }

    public override dispose(): void {
        this.structure.texture?.cropToContentBounds();
    }

    /**
     * A view of another Environment, or with none armed, gets the refusal
     * of {@link writeRefusal}; while strokes are confined to the
     * shown plane, a view that follows no image slice is refused with
     * the `contouring.refused.offImageSlice` reason.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this.environment, (own) =>
            this.isStrokeConfinedToPlane() && own.imageSliceAxis === undefined
                ? localization.t('contouring.refused.offImageSlice')
                : undefined
        );
    }

    /**
     * Whether the circle cursor may show on `view`: a view the tool accepts,
     * on a structure it may change, and the view of the stroke in progress
     * when there is one.
     */
    private showsCursorOn(view: PlaneView): boolean {
        return (
            this.structure.canModifyContours &&
            this.refusalFor(view) === undefined &&
            (this._activeView === undefined || view === this._activeView)
        );
    }

    private finishStroke(): EventHandlerResult {
        if (!this._state || !this._action) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        try {
            return this._action.stopAction(this._state);
        } finally {
            this.clearStroke();
        }
    }

    private clearStroke(): void {
        this._state = undefined;
        this._action = undefined;
        this._activeView = undefined;
    }
}

function clampDiameter(diameterMm: number): number {
    return Math.max(MIN_DIAMETER_MM, Math.min(MAX_DIAMETER_MM, diameterMm));
}

/**
 * Shift+Wheel step in mm, coarsening with the current diameter so the
 * whole 2–50 mm range is reachable in a comparable number of notches at
 * either end.
 */
function wheelStepMm(diameterMm: number): number {
    if (diameterMm <= 10) {
        return 2;
    }
    if (diameterMm <= 20) {
        return 4;
    }
    if (diameterMm <= 40) {
        return 6;
    }
    return 10;
}
